/**
 * Google Analytics (GA4) loader for the public site. Deliberately tiny + framework-free
 * so it can be driven by the consent store: we only ever call `loadGA` once the visitor
 * has granted analytics consent AND the super-admin has configured + enabled a tag.
 *
 * SPA note: gtag's `config` fires a single page_view on load. Client-side route changes
 * don't, so PublicAnalytics calls `trackPageview` on each navigation.
 */
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const SCRIPT_ATTR = 'data-ga';

/** Inject the gtag.js script + bootstrap config. Idempotent. */
export function loadGA(measurementId: string): void {
  if (typeof document === 'undefined') return;
  if (document.querySelector(`script[${SCRIPT_ATTR}]`)) return; // already loaded

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  // Expose cross-origin script errors to window.onerror (and thus PostHog
  // Error Tracking) instead of the scrubbed "Script error." blank.
  script.crossOrigin = 'anonymous';
  script.setAttribute(SCRIPT_ATTR, measurementId);
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // gtag pushes its own `arguments` object onto the dataLayer — keep the canonical shape.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  // We send page_view manually on every route (incl. the first) so SPA navigations
  // are captured; disable the automatic initial one to avoid a double-count.
  window.gtag('config', measurementId, { send_page_view: false });
}

/** Remove the gtag script + handle so a consent withdrawal stops collection on next load. */
export function unloadGA(): void {
  if (typeof document === 'undefined') return;
  document.querySelectorAll(`script[${SCRIPT_ATTR}]`).forEach((el) => el.remove());
  delete window.gtag;
  window.dataLayer = [];
}

/** Whether the gtag script is currently injected. */
export function isGALoaded(): boolean {
  return typeof document !== 'undefined' && !!document.querySelector(`script[${SCRIPT_ATTR}]`);
}

/** Send a GA4 page_view for a client-side navigation. */
export function trackPageview(measurementId: string, path: string): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: document.title,
    send_to: measurementId,
  });
}
